import { motion } from 'framer-motion'
import { UserX, Users, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const PlayerLeftModal = ({
  isVisible,
  leavingPlayerName,
  newCurrentPlayerName,
  remainingPlayerCount,
  onContinue,
  onQuit
}) => {
  const { t } = useTranslation('game')

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="glass-effect rounded-xl p-8 text-center max-w-md w-full mx-4"
      >
        {/* Alert Icon */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
              <UserX className="w-8 h-8 text-red-400" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('playerLeft.title')}</h2>
        </motion.div>

        {/* Player Left Message */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6 space-y-3"
        >
          <p className="text-gray-300 text-lg">
            {t('playerLeft.playerLeftMessage', { playerName: leavingPlayerName })}
          </p>

          {/* Show turn message if turn advanced */}
          {newCurrentPlayerName && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex items-center justify-center gap-2 p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30"
            >
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              <p className="text-yellow-200 font-medium">
                {t('playerLeft.newTurnMessage', { playerName: newCurrentPlayerName })}
              </p>
            </motion.div>
          )}

          {/* Remaining players count */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center justify-center gap-2 text-gray-400"
          >
            <Users className="w-5 h-5" />
            <span>{t('playerLeft.playersRemaining', { count: remainingPlayerCount })}</span>
          </motion.div>

          {/* Info message */}
          <p className="text-sm text-gray-500 mt-4">
            {t('playerLeft.gameWillContinue')}
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex gap-3"
        >
          <button
            onClick={onQuit}
            className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
          >
            {t('playerLeft.quitGame')}
          </button>
          <button
            onClick={onContinue}
            className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            {t('playerLeft.continueGame')}
          </button>
        </motion.div>

        {/* Pulsing Border Animation */}
        <motion.div
          className="absolute inset-0 border-2 border-red-400/50 rounded-xl pointer-events-none"
          animate={{
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>
    </div>
  )
}

export default PlayerLeftModal
